
import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { NodeData } from './types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Cog, Clipboard, Braces } from 'lucide-react';

// Define the WorkflowNode component with proper typing
const WorkflowNode = ({ data, selected }: NodeProps<NodeData>) => {
  // Safe data access with defaults
  const nodeType = data?.type || 'default';
  const nodeLabel = data?.label || 'Unknown Node';
  const configData = data?.config as Record<string, unknown> || {};
  
  // Helper function to get configuration summary
  const getConfigSummary = () => {
    if (!configData) return 'No configuration';
    
    // Check for different node types
    if (nodeType === 'capability' && configData.capability_name) {
      return `${configData.capability_name}`;
    } else if (nodeType === 'condition' && configData.condition) {
      return `${configData.condition}`;
    } else if (nodeType === 'input' && configData.variable_name) {
      return `${configData.variable_name}`;
    }
    
    return 'Configuration available';
  };

  return (
    <Card 
      className={`w-48 shadow-md ${selected ? 'ring-2 ring-primary' : ''}`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="w-2 h-2 !bg-primary"
      />
      
      <CardHeader className="py-2 px-3 bg-muted/50 border-b">
        <CardTitle className="text-sm flex items-center gap-1">
          {nodeType === 'capability' && <Cog className="h-4 w-4" />}
          {nodeType === 'condition' && <Braces className="h-4 w-4" />}
          {nodeType === 'input' && <Clipboard className="h-4 w-4" />}
          {nodeLabel}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-2 text-xs">
        <p className="text-muted-foreground">{getConfigSummary()}</p>
      </CardContent>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2 h-2 !bg-primary"
      />
    </Card>
  );
};

export default memo(WorkflowNode);
